import { useCallback, useState } from "react";

const PREFIX = "aco:view-state:v1";

function defaultStorage(): Storage | undefined {
  return typeof window === "undefined" ? undefined : window.localStorage;
}

export function viewStateKey(orgSlug: string, view: string, name: string) {
  return `${PREFIX}:${orgSlug}:${view}:${name}`;
}

export function loadViewState<T>(
  key: string,
  fallback: T,
  storage: Storage | undefined = defaultStorage(),
): T {
  if (!storage) return fallback;
  try {
    const raw = storage.getItem(key);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

export function saveViewState<T>(
  key: string,
  value: T,
  storage: Storage | undefined = defaultStorage(),
) {
  if (!storage) return;
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // Remembering is a convenience; a full or blocked store never breaks the view.
  }
}

/**
 * Per-device memory for one view's controls (sort, filters, layout), keyed by
 * workspace and view so By campaign and By creative each keep their own. Reads
 * once on mount; every change is written through. Switching key re-reads.
 */
export function useRememberedState<T>(key: string, fallback: T) {
  const [entry, setEntry] = useState(() => ({ key, value: loadViewState(key, fallback) }));
  const value = entry.key === key ? entry.value : loadViewState(key, fallback);
  const set = useCallback(
    (next: T | ((current: T) => T)) => {
      setEntry((current) => {
        const base = current.key === key ? current.value : loadViewState(key, fallback);
        const resolved = typeof next === "function" ? (next as (current: T) => T)(base) : next;
        saveViewState(key, resolved);
        return { key, value: resolved };
      });
    },
    [key, fallback],
  );
  return [value, set] as const;
}
